import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _get from "@babel/runtime/helpers/get";
import _inherits from "@babel/runtime/helpers/inherits";
import * as THREE from 'three';
import TiledGeometryLayer from "../../../Layer/TiledGeometryLayer";
import { ellipsoidSizes } from "../../Math/Ellipsoid";
import { globalExtentTMS, schemeTiles } from "../../Geographic/Extent";
import BuilderEllipsoidTile from "./BuilderEllipsoidTile";
import { SIZE_DIAGONAL_TEXTURE } from "../../../Process/LayeredMaterialNodeProcessing"; // matrix to convert sphere to ellipsoid

var worldToScaledEllipsoid = new THREE.Matrix4(); // camera's position in worldToScaledEllipsoid system

var cameraPosition = new THREE.Vector3();
var magnitudeSquared = 0.0; // vectors for operation purpose

var scaledHorizonCullingPoint = new THREE.Vector3();
/**
 * @property {boolean} isGlobeLayer - Used to checkout whether this layer is a
 * GlobeLayer. Default is true. You should not change this, as it is used
 * internally for optimisation.
 */

var GlobeLayer =
/*#__PURE__*/
function (_TiledGeometryLayer) {
  _inherits(GlobeLayer, _TiledGeometryLayer);

  /**
   * A {@link TiledGeometryLayer} to use with a {@link GlobeView}. It has
   * specific method for updating and subdivising its grid.
   *
   * @constructor
   * @extends TiledGeometryLayer
   *
   * @param {string} id - The id of the layer, that should be unique. It is
   * not mandatory, but an error will be emitted if this layer is added a
   * {@link View} that already has a layer going by that id.
   * @param {THREE.Object3d} [object3d=THREE.Group] - The object3d used to
   * contain the geometry of the TiledGeometryLayer. It is usually a
   * `THREE.Group`, but it can be anything inheriting from a `THREE.Object3d`.
   * @param {Object} [config] - Optional configuration, all elements in it
   * will be merged as is in the layer. For example, if the configuration
   * contains three elements `name, protocol, extent`, these elements will be
   * available using `layer.name` or something else depending on the property
   * name.
   * @param {number} [config.minSubdivisionLevel=2] - Minimum subdivision
   * level for this tiled layer.
   * @param {number} [config.maxSubdivisionLevel=18] - Maximum subdivision
   * level for this tiled layer.
   * @param {number} [config.sseSubdivisionThreshold=1] - Threshold level for
   * the SSE.
   * @param {number} [config.maxDeltaElevationLevel=4] - Maximum delta between
   * two elevations tile.
   *
   * @throws {Error} `object3d` must be a valid `THREE.Object3d`.
   */
  function GlobeLayer(id, object3d) {
    var _this;

    var config = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

    _classCallCheck(this, GlobeLayer);

    // Configure tiles
    var scheme = schemeTiles.get('WMTS:WGS84');
    var schemeTile = globalExtentTMS.get('EPSG:4326').subdivisionByScheme(scheme); // Supported tile matrix set for color/elevation layer

    config.tileMatrixSets = ['WMTS:WGS84', 'WMTS:PM'];
    var uvCount = config.tileMatrixSets.length;
    var builder = new BuilderEllipsoidTile({
      projection: 'EPSG:4978',
      uvCount: uvCount
    });
    _this = _possibleConstructorReturn(this, _getPrototypeOf(GlobeLayer).call(this, id, object3d || new THREE.Group(), schemeTile, builder, config));
    _this.isGlobeLayer = true;
    _this.options.defaultPickingRadius = 5;
    _this.minSubdivisionLevel = _this.minSubdivisionLevel || 2.0;
    _this.maxSubdivisionLevel = _this.maxSubdivisionLevel || 19.0;
    _this.maxDeltaElevation = _this.maxDeltaElevation || 4.0;
    _this.extent = _this.schemeTile[0].clone();

    for (var i = 1; i < _this.schemeTile.length; i++) {
      _this.extent.union(_this.schemeTile[i]);
    } // We're going to use the method described here:
    //    https://cesiumjs.org/2013/04/25/Horizon-culling/
    // This method assumes that the globe is a unit sphere at 0,0,0 so
    // we setup a world-to-scaled-ellipsoid matrix4


    worldToScaledEllipsoid.getInverse(_this.object3d.matrixWorld);
    worldToScaledEllipsoid.premultiply(new THREE.Matrix4().makeScale(1 / ellipsoidSizes.x, 1 / ellipsoidSizes.y, 1 / ellipsoidSizes.z));
    return _this;
  }

  _createClass(GlobeLayer, [{
    key: "preUpdate",
    value: function preUpdate(context, changeSources) {
      // pre-horizon culling
      cameraPosition.copy(context.camera.camera3D.position).applyMatrix4(worldToScaledEllipsoid);
      magnitudeSquared = cameraPosition.lengthSq() - 1.0;
      return _get(_getPrototypeOf(GlobeLayer.prototype), "preUpdate", this).call(this, context, changeSources);
    }
  }, {
    key: "countColorLayersTextures",
    value: function countColorLayersTextures() {
      var occupancy = 0;

      for (var _len = arguments.length, layers = new Array(_len), _key = 0; _key < _len; _key++) {
        layers[_key] = arguments[_key];
      }

      for (var _i = 0, _layers = layers; _i < _layers.length; _i++) {
        var layer = _layers[_i];
        var projection = layer.projection || layer.source.projection; // 'EPSG:3857' occupies the maximum 3 textures on tiles
        // 'EPSG:4326' occupies 1 textures on tile

        occupancy += projection == 'EPSG:3857' ? 3 : 1;
      }

      return occupancy;
    }
  }, {
    key: "culling",
    value: function culling(node, camera) {
      if (_get(_getPrototypeOf(GlobeLayer.prototype), "culling", this).call(this, node, camera)) {
        return true;
      }

      if (node.level < this.minSubdivisionLevel) {
        return false;
      } // see https://cesiumjs.org/2013/04/25/Horizon-culling/


      scaledHorizonCullingPoint.copy(node.horizonCullingPointElevationScaled).applyMatrix4(worldToScaledEllipsoid);
      scaledHorizonCullingPoint.sub(cameraPosition);
      var vtMagnitudeSquared = scaledHorizonCullingPoint.lengthSq();
      var dot = -scaledHorizonCullingPoint.dot(cameraPosition);
      var isOccluded = magnitudeSquared < 0 ? dot > 0 : magnitudeSquared < dot && magnitudeSquared < dot * dot / vtMagnitudeSquared;
      return isOccluded;
    }
  }, {
    key: "computeTileZoomFromDistanceCamera",
    value: function computeTileZoomFromDistanceCamera(distance, camera) {
      var preSinus = SIZE_DIAGONAL_TEXTURE * (this.sseSubdivisionThreshold * 0.5) / camera._preSSE / ellipsoidSizes.x;
      var sinus = distance * preSinus;
      var zoom = Math.log(Math.PI / (2.0 * Math.asin(sinus))) / Math.log(2);
      var delta = Math.PI / Math.pow(2, zoom);
      var circleChord = 2.0 * ellipsoidSizes.x * Math.sin(delta * 0.5);
      // adjust with bounding sphere rayon
      sinus = (distance - circleChord * 0.5) * preSinus;
      zoom = Math.log(Math.PI / (2.0 * Math.asin(sinus))) / Math.log(2);
      return isNaN(zoom) ? 0 : Math.round(zoom);
    }
  }, {
    key: "computeDistanceCameraFromTileZoom",
    value: function computeDistanceCameraFromTileZoom(zoom, camera) {
      var delta = Math.PI / Math.pow(2, zoom);
      var circleChord = 2.0 * ellipsoidSizes.x * Math.sin(delta * 0.5);
      var radius = circleChord * 0.5;
      return camera._preSSE * (radius / SIZE_DIAGONAL_TEXTURE) / (this.sseSubdivisionThreshold * 0.5) + radius;
    }
  }]);

  return GlobeLayer;
}(TiledGeometryLayer);

export default GlobeLayer;